import {
  createCampaign,
  deleteCampaign,
  restoreDeletedCampaign,
  setFavoriteCampaign,
  setCampaignVisibility,
  updateCampaign,
  updateCampaignAdditionalPermissions
} from '@api/Campaign';

import {
  CampaignItemDto,
  RestoreCampaignDto,
  SetCampaignFavoriteDto,
  SetCampaignVisibilityDto,
  UpdateCampaignDto,
  UpdateCampaignAdditionalPermissionsDto,
  CreateCampaignSuccessDto
} from '@api/Api';
import { queryClient } from 'src/query-client';
import { Dispatch } from 'react';
import { CampaignActionTypes } from '@redux/types/campaignType';
import {
  optimisticListItemRemove,
  optimisticListItemUpdate
} from '@hooks/queries/helpers';

export const setFavoriteCampaignAction =
  (params: SetCampaignFavoriteDto) => async () => {
    await queryClient.cancelQueries({ queryKey: ['campaigns', 'list'] });
    optimisticListItemUpdate<CampaignItemDto>(
      { queryKey: ['campaigns', 'list'], id: params.campaignId },
      (draft) => {
        draft.isFavorite = params.isFavorite;
      }
    );
    await setFavoriteCampaign(params);
    queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] });
  };

export const removeCampaign =
  (params: { campaignId: string; permanent: boolean }) => async () => {
    await deleteCampaign(params);
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  };

export const restoreRemovedCampaign =
  (params: RestoreCampaignDto) => async () => {
    await queryClient.cancelQueries({ queryKey: ['campaigns'] });
    optimisticListItemRemove<CampaignItemDto>({
      queryKey: ['campaigns', 'list'],
      id: params.campaignId
    });
    await restoreDeletedCampaign(params);
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  };

export const createNewCampaign =
  (data: {
    forOnboarding?: boolean | null;
    workspaceId: string;
    name: string;
    isPrivate: boolean;
  }) =>
  async (
    dispatch: Dispatch<{
      type: CampaignActionTypes.CREATE_CAMPAIGN;
      payload: { campaign: CampaignItemDto };
    }>
  ): Promise<CreateCampaignSuccessDto> => {
    const {
      data: { campaign, defaultTaskBoardIds }
    } = await createCampaign({
      ...data
    });
    dispatch({
      type: CampaignActionTypes.CREATE_CAMPAIGN,
      payload: { campaign }
    });
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    return { campaign, defaultTaskBoardIds };
  };

export const updateAdditionalPermissions =
  (params: UpdateCampaignAdditionalPermissionsDto) => async () => {
    await updateCampaignAdditionalPermissions(params);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'item'] })
    ]);
  };

export const setCampaignVisibilityAction = async (
  params: SetCampaignVisibilityDto
) => {
  await setCampaignVisibility(params);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    queryClient.invalidateQueries({ queryKey: ['campaigns', 'item'] })
  ]);
};

export const updateCampaignDetails =
  (params: UpdateCampaignDto) => async () => {
    await updateCampaign(params);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'item'] })
    ]);
  };

export const renameCampaign =
  ({ id, name }: { id: string; name: string }) =>
  async () => {
    await updateCampaign({ id, name });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'item'] })
    ]);
  };
